import { useCallback, useState } from 'react';
import Calendar, { CalendarProvider } from 'components/features/Calendar';
import moment, { Moment } from 'moment';
import { Grid } from '@mui/material';
import useStyles from './Organizer.styles';
import { useQuery } from '@apollo/client';
import {
  GET_EVENTS_QUERY,
  GetEventsOutput,
} from 'graphql/event/EventQueries';
import { useSelectedLinkContext } from 'contexts';
import DayContent from './Organizer/DayContent';
import CalendarUtils from 'utils/calendar';
import {
  EventsAndTimeslots,
  Event,
  isEvent,
} from 'types/Event';
import { OrganizerContextProvider } from './OrganizerContext';
import DayContainer from './Organizer/DayContainer';
import EventForm from './Organizer/EventForm';
import { DayContentMode } from 'types/Organizer';
import DeleteEventModal from './Organizer/DayContent/DayEvent/DeleteEventModal';

type OrganizerPureProps = {
  shouldShowEventForm: boolean;
};

const OrganizerPure = ({
  shouldShowEventForm,
}: OrganizerPureProps): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <Grid className={classes.container} container spacing={6}>
        <Grid className={classes.calendarContainer} item md={8}>
          <Calendar />
        </Grid>

        <Grid className={classes.dayContainer} item md={4}>
          <DayContainer>
            <DayContent />

            {shouldShowEventForm && <EventForm />}
          </DayContainer>
        </Grid>
      </Grid>

      <DeleteEventModal />
    </>
  );
};

const getSelectedDayEventsAndTimeslots = (
  currentMonthEventsAndTimeslots: EventsAndTimeslots,
  selectedDate: Moment | undefined,
): EventsAndTimeslots => {
  if (selectedDate) {
    return currentMonthEventsAndTimeslots
      .filter((eventOrTimeslot) =>
        CalendarUtils.isSameDate(moment(eventOrTimeslot.date), selectedDate),
      )
      .sort((a, b) => (a.date < b.date ? -1 : 1));
  } else {
    return [];
  }
};

const getCurrentMonthEventsAndTimeslots = (
  eventsAndTimeslots?: EventsAndTimeslots,
): EventsAndTimeslots => {
  if (eventsAndTimeslots) {
    return eventsAndTimeslots.map((eventOrTimeslot) => {
      if (isEvent(eventOrTimeslot) && eventOrTimeslot.daysOfRepeat) {
        const [sunday, monday, tuesday, wednesday, thursday, friday, saturday] =
          eventOrTimeslot.daysOfRepeat;

        return {
          ...eventOrTimeslot,
          daysOfRepeat: [
            monday,
            tuesday,
            wednesday,
            thursday,
            friday,
            saturday,
            sunday,
          ],
        };
      } else {
        return eventOrTimeslot;
      }
    });
  } else {
    return [];
  }
};

const Organizer = (): JSX.Element | null => {
  const { selectedLink } = useSelectedLinkContext();
  const [currentDate, setCurrentDate] = useState<Moment>(moment());
  const [selectedDate, setSelectedDate] = useState<Moment | undefined>(
    moment(),
  );
  const [dayContentMode, setDayContentMode] = useState<
    DayContentMode | undefined
  >(undefined);
  const [eventToDelete, setEventToDelete] = useState<Event>();
  const [eventToEdit, setEventToEdit] = useState<Event>();

  const { data } = useQuery<GetEventsOutput>(GET_EVENTS_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      getEventsInput: {
        channel: selectedLink?.channelKey,
        month: currentDate.month() + 1,
        year: currentDate.year(),
      },
    },
  });

  const handleSelectedDateChange = useCallback(
    (newSelectedDate: Moment | undefined) => {
      setSelectedDate(newSelectedDate);
      setDayContentMode(undefined);
    },
    [setDayContentMode, setSelectedDate],
  );

  const currentMonthEventsAndTimeslots = getCurrentMonthEventsAndTimeslots(
    data?.getEvents?.filteredEvents,
  );
  const selectedDayEventsAndTimeslots = getSelectedDayEventsAndTimeslots(
    currentMonthEventsAndTimeslots,
    selectedDate,
  );

  const shouldShowEventForm = dayContentMode !== undefined;

  return (
    <OrganizerContextProvider
      dayContentMode={dayContentMode}
      eventToDelete={eventToDelete}
      eventToEdit={eventToEdit}
      setDayContentMode={setDayContentMode}
      setEventToDelete={setEventToDelete}
      setEventToEdit={setEventToEdit}
    >
      <CalendarProvider
        currentDate={currentDate}
        currentMonthEventsAndTimeslots={currentMonthEventsAndTimeslots}
        disabled={!!dayContentMode}
        selectedDate={selectedDate}
        selectedDayEventsAndTimeslots={selectedDayEventsAndTimeslots}
        setCurrentDate={setCurrentDate}
        handleSelectedDateChange={handleSelectedDateChange}
      >
        <OrganizerPure shouldShowEventForm={shouldShowEventForm} />
      </CalendarProvider>
    </OrganizerContextProvider>
  );
};

export default Organizer;
